import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { Spinner } from "../components/Spinner";
import { useForm } from "../hooks/useForm";
import {
  useDocumentoAdicionalMutation,
  useEliminardocAdiMutation,
  useLazyGetDocumentosAdicionalesQuery,
} from "../services/docs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/Acordion";

export const DocumentacionAdicionalComponent = () => {
  const { siniestro = "0", tramite_id = "0" } = useParams();

  // RTK Queries & Mutations
  const [documentos, { data: doc, isLoading }] = useLazyGetDocumentosAdicionalesQuery();
  const [documento, { isLoading: cargandoBoton }] = useDocumentoAdicionalMutation();
  const [eliminar] = useEliminardocAdiMutation();

  // Estado para saber qué tarjeta exacta se está eliminando
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { formulario, handleInputChange, handleTexareChange, reset } = useForm({
    tramite_id: +tramite_id,
    fc_siniestro: siniestro,
    fc_nombre_documento: "",
    fc_descripcion_documento: "",
  });

  useEffect(() => {
    documentos({ siniestro, tramite: tramite_id });
  }, [siniestro, tramite_id, documentos]);

  const eliminarDoc = async (id: number) => {
    setDeletingId(id);
    try {
      const resp = await eliminar({
        tramite_id: +tramite_id,
        fc_siniestro: siniestro,
        id: id,
      }).unwrap();

      if (resp) {
        toastr.success(resp.message || "Documento eliminado correctamente");
        documentos({ siniestro, tramite: tramite_id });
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error("Error al eliminar el documento");
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleAction = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!formulario.fc_nombre_documento.trim()) {
      toastr.warning("Debes asignar un nombre al documento");
      return;
    }

    try {
      const resp = await documento({
        ...formulario,
        fi_dictamen_id: 1, // Valor por defecto de tu lógica original
      }).unwrap();

      if (resp) {
        toastr.success(resp.message || "Documento solicitado con éxito");
        reset({
          tramite_id: +tramite_id,
          fc_siniestro: siniestro,
          fc_nombre_documento: "",
          fc_descripcion_documento: "",
        });
        documentos({ siniestro, tramite: tramite_id });
      }
    } catch (err: any) {
      if (err?.status === 400 && err?.data?.message) {
        if (Array.isArray(err.data.message)) {
          err.data.message.forEach((m: string) => toastr.error(m));
        } else {
          toastr.error(err.data.message);
        }
      } else {
        toastr.error("Error al solicitar el documento");
      }
    }
  };

  if (isLoading && !doc) {
    return (
      <div className="flex w-full items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  const listadoDocs = doc?.misDocumentosAdicionales || [];

  return (
    <Accordion type="single" collapsible className="w-full rounded-xl border border-slate-200 bg-white shadow-sm">
      <AccordionItem value="activos" className="border-b-0">

        {/* --- HEADER DEL ACORDEÓN --- */}
        <AccordionTrigger className="flex w-full items-center justify-between px-5 py-4 hover:bg-slate-50 hover:no-underline data-[state=open]:border-b data-[state=open]:border-slate-100">
          <div className="flex items-center gap-2">
            <div className="h-4 w-1.5 rounded bg-slate-300"></div>
            <h3 className="text-sm font-bold text-slate-800">
              Documentos Adicionales / Complementarios
            </h3>
            <span className="font-mono text-xs font-normal text-slate-400">
              ({listadoDocs.length} registrados)
            </span>
          </div>
        </AccordionTrigger>

        {/* --- CONTENIDO DESPLEGABLE --- */}
        <AccordionContent className="px-5 py-6 text-sm">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start">

            {/* 1. Formulario de Solicitud (Columna Izquierda 1/3) */}
            <div className="w-full rounded-xl border border-slate-100 bg-slate-50/50 p-5 lg:w-1/3">
              <h4 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600">
                <svg className="h-4 w-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                Solicitar Documento
              </h4>

              <form onSubmit={handleAction} className="space-y-4">
                <div>
                  <label htmlFor="fc_nombre_documento" className="mb-1 block text-[11px] font-semibold text-slate-600">
                    Nombre del Documento
                  </label>
                  <input
                    type="text"
                    id="fc_nombre_documento"
                    name="fc_nombre_documento"
                    placeholder="Ej. Factura del taller..."
                    onChange={handleInputChange}
                    value={formulario.fc_nombre_documento}
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-xs font-medium text-slate-800 transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
                <div>
                  <label htmlFor="fc_descripcion_documento" className="mb-1 block text-[11px] font-semibold text-slate-600">
                    Descripción / Motivo
                  </label>
                  <textarea
                    id="fc_descripcion_documento"
                    name="fc_descripcion_documento"
                    placeholder="Justificación del requerimiento..."
                    onChange={handleTexareChange}
                    value={formulario.fc_descripcion_documento}
                    rows={3}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-800 transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={cargandoBoton}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
                  >
                    {cargandoBoton ? <Spinner /> : (
                      <>
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                        Agregar a la lista
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* 2. Grid de Documentos Registrados (Columna Derecha 2/3) */}
            <div className="w-full lg:w-2/3">
              {listadoDocs.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/50 py-12 text-center">
                  <svg className="mb-3 h-8 w-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                  <p className="text-sm font-bold text-slate-500">No hay documentos adicionales</p>
                  <p className="mt-1 text-xs text-slate-400">Agrega un requerimiento en el formulario para solicitarlo al asegurado.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {listadoDocs.map((d: any) => (
                    <article key={d.id} className="group relative flex flex-col justify-between overflow-hidden rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-card">

                      {/* Botón Eliminar Absoluto */}
                      <button
                        onClick={() => eliminarDoc(d.id)}
                        disabled={deletingId === d.id}
                        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                        title="Eliminar Requerimiento"
                      >
                        {deletingId === d.id ? <Spinner /> : (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                        )}
                      </button>

                      <div>
                        <div className="mb-2 flex items-center gap-1.5">
                          <span className="flex h-1.5 w-1.5 rounded-full bg-amber-500"></span>
                          <span className="text-[10px] font-semibold uppercase text-slate-500">Requerimiento Extra</span>
                        </div>
                        <h5 className="pr-8 text-xs font-bold leading-snug text-slate-800">
                          {d.fc_nombre_documento}
                        </h5>
                        <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
                          {d.fc_descripcion_documento}
                        </p>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
